/**
 * ReviewCarousel.jsx
 *
 * Swiper-based carousel for displaying game reviews
 * - Autoplay with 5-second delay
 * - Touch/swipe support
 * - Pagination dots
 * - Navigation arrows
 * - Responsive breakpoints (1/2/3 slides)
 *
 * Props:
 * - reviews: array - Array of review objects { id, playerName, rating, comment, timestamp }
 */

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { useLanguage } from '../contexts/LanguageContext';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const ReviewCarousel = ({ reviews }) => {
  const { t, language } = useLanguage();

  if (!reviews || reviews.length === 0) {
    return (
      <div className="review-carousel-empty">
        <p>{t('reviews.noReviews')}</p>
      </div>
    );
  }

  /**
   * Format date based on current language
   */
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(language, options);
  };

  /**
   * Render star rating
   */
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`review-star ${i <= rating ? 'filled' : 'empty'}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="review-carousel-container">
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        spaceBetween={30}
        slidesPerView={1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        navigation={reviews.length > 1}
        loop={reviews.length > 2}
        breakpoints={{
          // All screen sizes: 1 card at a time (container max-width is 500px)
          320: {
            slidesPerView: 1,
            spaceBetween: 0,
          },
        }}
        className="review-swiper"
      >
        {reviews.map((review) => (
          <SwiperSlide key={review.id}>
            <div className="review-carousel-slide">
              {/* Star Rating */}
              <div className="review-slide-rating">{renderStars(review.rating)}</div>

              {/* Comment */}
              <p className="review-slide-comment">"{review.comment}"</p>

              {/* Author and Date */}
              <div className="review-slide-footer">
                <div className="review-slide-author">
                  <strong>{review.playerName}</strong>
                </div>
                <div className="review-slide-date">
                  {formatDate(review.timestamp || review.createdAt)}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default ReviewCarousel;
